import { gql, useQuery } from "@apollo/client";
import { midgardClients } from "providers/MidgardProvider";
import { useState } from "react";
import { ReportParams } from "../types";
import { MetricEnum } from "./metricTypes";
import { useParams } from "react-router";
import { useCustomerInfoJsonAtom } from "./hooks/useCustomerInfoJson";
import { useProcessGroupNameAndMachemetryIds } from "reports/components/Reports/reportUtils";

const GENERAL_CONFIG_QUERY = gql`
  {
    generalConfig {
      id
      created
      modified
      start
      end
      timeZone
      targetConfigs
    }
  }
`;

export const useFetchCustomerTimezone = () => {
  const [timeZone, setTimeZone] = useState<string | null>(null);
  const { ckey: tenant } = useParams() as ReportParams;

  const { loading, error } = useQuery(GENERAL_CONFIG_QUERY, {
    client: midgardClients.get(tenant),
    onCompleted: data => {
      if (data?.generalConfig?.timeZone && !loading && !error) {
        setTimeZone(data.generalConfig.timeZone);
      }
    },
  });
  return timeZone;
};

export const useFetchGeneralConfig = () => {
  const [config, setConfig] = useState<any | null>(null);
  const { ckey: tenant } = useParams() as ReportParams;

  const { loading, error } = useQuery(GENERAL_CONFIG_QUERY, {
    client: midgardClients.get(tenant),
    onCompleted: data => {
      if (data?.generalConfig && !loading && !error) {
        setConfig(data.generalConfig);
      }
    },
  });
  return config;
};

export const useTimezoneFromCustomerJson = () => {
  const customerJson = useCustomerInfoJsonAtom();
  const timeZone = customerJson?.timeZone;
  return timeZone;
};

export const useTargetsFromCustomerJson = () => {
  const customerJson = useCustomerInfoJsonAtom();
  const { processGroupName } = useProcessGroupNameAndMachemetryIds();
  const thresholds = customerJson?.thresholds;
  const thresholdsOverride =
    customerJson?.thresholdsByProcessGroup?.[processGroupName];

  // thresholdsOverride is a partial override so we need to fallback for each metric below
  const groupOrTenantThreshold = metric =>
    thresholdsOverride?.[metric] ?? thresholds?.[metric];

  return {
    [MetricEnum.OEE]: groupOrTenantThreshold("OEE"),
    [MetricEnum.TEEP]: groupOrTenantThreshold("TEEP"),
    [MetricEnum.UPTIME]: groupOrTenantThreshold("Uptime") ?? 0.9,
    [MetricEnum.AVAIL]: groupOrTenantThreshold("Availability"),
    [MetricEnum.TAGGING]: groupOrTenantThreshold("Tagging"),
    [MetricEnum.PERF]: groupOrTenantThreshold("Performance"),
  };
};
